/**
 * KisiFiltreleri.jsx — Kişi Listesi "Filtreler" paneli (backend #120, backlog #121)
 *
 * Tamamı kontrollü: `suzgec` nesnesi services/kisiListService.js'teki BOS_SUZGEC şeklindedir ve
 * uç parametrelerine 1:1 eşlenir. Uygulama/debounce sayfanın işi — burada yalnızca düzenlenir.
 *
 * Fuar gerektiren üç süzgeç (kart durumu, kart seviyesi, firma tipi) fuar görünümünde (fuarId) açıktır:
 * uç bunları fuarsız 400 ile reddediyor. Kart basımı ve KKD (#359/#373) fuarId YA DA üst çubuk fuarı
 * (aktifFuarId) ile çalışır, yani Tüm Kişiler'de de üst çubukta fuar varsa açıktır. "Aktif fuara göre"
 * (#359) yalnızca Tüm Kişiler'de, Firma evrakları seçiliyken ve üst çubuk fuarı varken görünür.
 *
 * "Basılmış + KKD uygun" kendi içinde KKD koşulu taşır; uç bunu kkd ile birlikte kabul etmez (400),
 * bu yüzden ikisinden biri seçilince diğeri uyumlu hale getirilir.
 *
 * Props: suzgec · onChange(next) · fuarId (number|null) · aktifFuarId (number|null) · aktifSayi · onTemizle
 */
import React from "react";
import { Row, Col, Input, Label, Button, ButtonGroup } from "reactstrap";
import { X } from "lucide-react";
import FirmaSearchSelect from "../Firma/FirmaSearchSelect";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { KART_DURUMU } from "../../services/kartDurumuService";
import {
  BELGE_SUZGEC_ANAHTARLARI, FIRMA_TIPI_SECENEKLERI, KART_SEVIYESI_SECENEKLERI,
  KART_BASIM_SECENEKLERI, KKD_SECENEKLERI, KART_BASILDI_KKD_UYGUN, KIMLIK_TIPI_SECENEKLERI,
} from "../../services/kisiListService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// Bu temada `success` gri, `secondary` yeşilimsi (bkz. StandPills.STATUS_HEX notu): anlamsal
// yeşil/kırmızı için tema sınıfları değil, belge karelerinin kullandığı sabit renkler.
// Dört durum (#271/#272), belge karelerinin renkleriyle aynı: Onaylı yeşil, Bekliyor sarı, Yok kırmızı.
const BELGE_DURUM = [
  { value: "", get label() { return i18n.t("components:kisiFiltreleri.hepsi"); }, hex: NEUTRAL_GRAY },
  { value: "var", get label() { return i18n.t("components:kisiFiltreleri.onayli"); }, hex: STATUS_HEX.success },
  { value: "bekliyor", get label() { return i18n.t("components:kisiFiltreleri.bekliyor"); }, hex: STATUS_HEX.warning, ink: "#212529" },
  { value: "yok", get label() { return i18n.t("components:kisiFiltreleri.yok"); }, hex: STATUS_HEX.danger },
];

function BelgeDurumlu({ value, onChange }) {
  return (
    <ButtonGroup size="sm" className="w-100">
      {BELGE_DURUM.map((o) => {
        const secili = value === o.value;
        return (
          <Button
            key={o.value} color="light" className="text-nowrap px-2"
            style={{ background: secili ? o.hex : "transparent", borderColor: o.hex, color: secili ? (o.ink || "#fff") : o.hex }}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

/** `sag`: etiket satırının sağına küçük bir denetim (ör. "Aktif fuara göre"); satır yüksekliğini oynatmaz. */
function Alan({ label, sag = null, children }) {
  return (
    <>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1" style={{ minHeight: 21 }}>
        <Label className="form-label small text-muted mb-0">{label}</Label>
        {sag}
      </div>
      {children}
    </>
  );
}

export default function KisiFiltreleri({ suzgec, onChange, fuarId, aktifFuarId = null, aktifSayi, onTemizle }) {
  const { t } = useTranslation("components");
  const set = (patch) => onChange({ ...suzgec, ...patch });
  const setBelge = (key, value) => {
    const belge = { ...suzgec.belge };
    if (value) belge[key] = value; else delete belge[key];
    set({ belge });
  };
  const fuarIpucu = fuarId ? undefined : t("kisiFiltreleri.buSuzgecIcinFuarSecin");
  // Kart basımı / KKD: fuar görünümünün fuarı ya da (Tüm Kişiler'de) üst çubuk fuarı.
  const kartFuarVar = Boolean(fuarId || aktifFuarId);
  const kartFuarIpucu = kartFuarVar ? undefined : t("kisiFiltreleri.buSuzgecIcinFuarSecin");
  // "Aktif fuara göre": uç fuarId'li istekte 400 verir, firmaBelgeTam'sız da anlamsız.
  const aktifFuarGoster = !fuarId && Boolean(aktifFuarId) && Boolean(suzgec.firmaBelgeTam);

  // Basılmış + KKD uygun kendi KKD koşulunu taşır; ayrı KKD seçimiyle birlikte olmaz (uç 400).
  const setKartBasim = (kartBasim) => set(kartBasim === KART_BASILDI_KKD_UYGUN ? { kartBasim, kkd: "" } : { kartBasim });
  const setKkd = (kkd) => set(
    kkd && suzgec.kartBasim === KART_BASILDI_KKD_UYGUN ? { kkd, kartBasim: "basildi" } : { kkd },
  );
  // Firma evrakları kaldırılınca anahtarı da kapanır (tek başına anlamı yok).
  const setFirmaBelgeTam = (firmaBelgeTam) => set(firmaBelgeTam ? { firmaBelgeTam } : { firmaBelgeTam, firmaBelgeAktifFuar: false });

  return (
    <>
      <Row className="g-3">
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.kimlikDogrulama")}>
            <Input type="select" bsSize="sm" value={suzgec.kimlik} onChange={(e) => set({ kimlik: e.target.value })}>
              <option value="">{t("common:tumu")}</option>
              <option value="dogrulandi">{t("kisiFiltreleri.dogrulandi")}</option>
              <option value="dogrulanmadi">{t("kisiFiltreleri.dogrulanmadi")}</option>
              <option value="alanda">{t("kisiFiltreleri.alandaDogrulanacak")}</option>
            </Input>
          </Alan>
        </Col>
        {/* #470: kimlik doğrulamadan ayrı süzgeç; ikisi birlikte seçilirse AND'lenir. */}
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.kimlikDogrulamaTipi")}>
            <Input type="select" bsSize="sm" value={suzgec.kimlikTipi || ""} onChange={(e) => set({ kimlikTipi: e.target.value })}>
              <option value="">{t("common:tumu")}</option>
              {KIMLIK_TIPI_SECENEKLERI.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.belgeDurumu")}>
            <Input type="select" bsSize="sm" value={suzgec.eksikBelge} onChange={(e) => set({ eksikBelge: e.target.value })}>
              <option value="">{t("common:tumu")}</option>
              <option value="true">{t("kisiFiltreleri.eksikBelgesiOlanlar")}</option>
              <option value="false">{t("kisiFiltreleri.belgesiTamOlanlar")}</option>
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.kartDurumu")}>
            <Input
              type="select" bsSize="sm" value={suzgec.kartDurumu} disabled={!fuarId} title={fuarIpucu}
              onChange={(e) => set({ kartDurumu: e.target.value })}
            >
              <option value="">{t("common:tumu")}</option>
              {Object.entries(KART_DURUMU).map(([key, d]) => (
                <option key={key} value={key}>{d.label}</option>
              ))}
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.kartSeviyesi")}>
            <Input
              type="select" bsSize="sm" value={suzgec.kartSeviyesi || ""} disabled={!fuarId} title={fuarIpucu}
              onChange={(e) => set({ kartSeviyesi: e.target.value })}
            >
              <option value="">{t("kisiFiltreleri.farkEtmez")}</option>
              {KART_SEVIYESI_SECENEKLERI.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.firmaTipi")}>
            <Input
              type="select" bsSize="sm" value={suzgec.firmaTipi} disabled={!fuarId} title={fuarIpucu}
              onChange={(e) => set({ firmaTipi: e.target.value })}
            >
              <option value="">{t("common:tumu")}</option>
              {FIRMA_TIPI_SECENEKLERI.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan
            label={t("kisiFiltreleri.firmaEvraklari")}
            sag={aktifFuarGoster && (
              <div className="d-flex align-items-center gap-1">
                <Input
                  type="checkbox" id="firmaBelgeAktifFuar" className="mt-0"
                  checked={Boolean(suzgec.firmaBelgeAktifFuar)}
                  onChange={(e) => set({ firmaBelgeAktifFuar: e.target.checked })}
                />
                <label className="form-check-label small text-nowrap mb-0" htmlFor="firmaBelgeAktifFuar">
                  {t("kisiFiltreleri.aktifFuaraGore")}
                </label>
              </div>
            )}
          >
            <Input type="select" bsSize="sm" value={suzgec.firmaBelgeTam || ""} onChange={(e) => setFirmaBelgeTam(e.target.value)}>
              <option value="">{t("kisiFiltreleri.farkEtmez")}</option>
              <option value="true">{t("kisiFiltreleri.tam")}</option>
              <option value="false">{t("kisiFiltreleri.eksik")}</option>
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.kartBasimi")}>
            <Input
              type="select" bsSize="sm" value={suzgec.kartBasim || ""} disabled={!kartFuarVar} title={kartFuarIpucu}
              onChange={(e) => setKartBasim(e.target.value)}
            >
              <option value="">{t("kisiFiltreleri.farkEtmez")}</option>
              {KART_BASIM_SECENEKLERI.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Input>
          </Alan>
        </Col>
        <Col xl={3} md={6}>
          <Alan label={t("kisiFiltreleri.kkd")}>
            <Input
              type="select" bsSize="sm" value={suzgec.kkd || ""} disabled={!kartFuarVar} title={kartFuarIpucu}
              onChange={(e) => setKkd(e.target.value)}
            >
              <option value="">{t("kisiFiltreleri.farkEtmez")}</option>
              {KKD_SECENEKLERI.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Input>
          </Alan>
        </Col>

        <Col xl={4} md={12}>
          <Alan label={t("kisiFiltreleri.firma")}>
            <FirmaSearchSelect
              value={suzgec.firma} onChange={(firma) => set({ firma })}
              placeholder={t("kisiFiltreleri.firmaAdiKisaAdiVeya")}
            />
          </Alan>
        </Col>
      </Row>

      {/* Dört düğme sığmazsa satır sarılır: xxl'de beşli değil dörtlü dizilim */}
      <Row xs={1} sm={2} lg={3} xxl={4} className="g-3 mt-0">
        {BELGE_SUZGEC_ANAHTARLARI.map((b) => (
          <Col key={b.key}>
            <Alan label={b.label}>
              <BelgeDurumlu value={suzgec.belge?.[b.key] || ""} onChange={(v) => setBelge(b.key, v)} />
            </Alan>
          </Col>
        ))}
      </Row>

      {aktifSayi > 0 && (
        <div className="d-flex justify-content-end mt-3">
          <Button color="light" size="sm" onClick={onTemizle}>
            <X size={13} className="me-1" />{t("kisiFiltreleri.filtreleriTemizle")}
          </Button>
        </div>
      )}
    </>
  );
}
